/**
 * Visitor Detection Hook
 * Detects visitor type based on various signals
 */

import { useState, useEffect, useCallback } from 'react';
import { MarketingVariant } from '../routes/MarketingRouter';

interface VisitorSignals {
  referrer: string;
  searchParams: URLSearchParams;
  userAgent: string;
  timezone: string;
  language: string;
  screenResolution: string;
  cookieData?: {
    previousVisits?: string[];
    lastVariant?: MarketingVariant;
    interests?: string[];
  };
}

interface DetectionResult {
  variant: MarketingVariant;
  confidence: number;
  signals: string[];
}

export const useVisitorDetection = () => {
  const [detectedVariant, setDetectedVariant] = useState<MarketingVariant>('default');
  const [confidence, setConfidence] = useState<number>(0);
  const [signals, setSignals] = useState<string[]>([]);

  /**
   * Collect visitor signals from various sources
   */
  const collectSignals = useCallback((): VisitorSignals => {
    const searchParams = new URLSearchParams(window.location.search);

    // Try to read existing cookie data
    let cookieData = {};
    try {
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('sigma08_visitor='))
        ?.split('=')[1];

      if (cookieValue) {
        cookieData = JSON.parse(decodeURIComponent(cookieValue));
      }
    } catch (e) {
      console.debug('No visitor cookie found');
    }

    return {
      referrer: document.referrer,
      searchParams,
      userAgent: navigator.userAgent,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      cookieData,
    };
  }, []);

  /**
   * Analyze signals to determine visitor type
   */
  const analyzeSignals = useCallback((signals: VisitorSignals): DetectionResult => {
    const detectedSignals: string[] = [];
    let educatorScore = 0;
    let parentScore = 0;
    let administratorScore = 0;

    // Check URL parameters
    const utm_source = signals.searchParams.get('utm_source');
    const utm_campaign = signals.searchParams.get('utm_campaign');
    const role = signals.searchParams.get('role');
    const source = signals.searchParams.get('source');

    if (role) {
      detectedSignals.push(`URL parameter: role=${role}`);
      switch (role) {
        case 'teacher':
        case 'educator':
          educatorScore += 100;
          break;
        case 'parent':
        case 'family':
          parentScore += 100;
          break;
        case 'admin':
        case 'administrator':
        case 'principal':
          administratorScore += 100;
          break;
      }
    }

    // Check UTM parameters
    if (utm_source) {
      detectedSignals.push(`UTM source: ${utm_source}`);
      if (utm_source.includes('teacher') || utm_source.includes('edu')) {
        educatorScore += 30;
      } else if (utm_source.includes('parent') || utm_source.includes('family')) {
        parentScore += 30;
      } else if (utm_source.includes('district') || utm_source.includes('admin')) {
        administratorScore += 30;
      }
    }

    if (utm_campaign) {
      detectedSignals.push(`UTM campaign: ${utm_campaign}`);
      if (utm_campaign.includes('teacher') || utm_campaign.includes('classroom')) {
        educatorScore += 20;
      } else if (utm_campaign.includes('parent') || utm_campaign.includes('home')) {
        parentScore += 20;
      } else if (utm_campaign.includes('district') || utm_campaign.includes('enterprise')) {
        administratorScore += 20;
      }
    }

    // Check referrer patterns
    if (signals.referrer) {
      const referrerDomain = new URL(signals.referrer).hostname;
      detectedSignals.push(`Referrer: ${referrerDomain}`);

      // Educational sites
      if (
        referrerDomain.includes('.edu') ||
        referrerDomain.includes('teacher') ||
        referrerDomain.includes('educator') ||
        referrerDomain.includes('edtech')
      ) {
        educatorScore += 25;
      }

      // Parent/family sites
      if (
        referrerDomain.includes('parent') ||
        referrerDomain.includes('family') ||
        referrerDomain.includes('mom') ||
        referrerDomain.includes('dad')
      ) {
        parentScore += 25;
      }

      // Administrative/district sites
      if (
        referrerDomain.includes('district') ||
        referrerDomain.includes('superintendent') ||
        referrerDomain.includes('k12') ||
        referrerDomain.includes('admin')
      ) {
        administratorScore += 25;
      }

      // Social media patterns
      if (referrerDomain.includes('facebook.com')) {
        parentScore += 10; // Parents more likely from Facebook
      } else if (referrerDomain.includes('linkedin.com')) {
        educatorScore += 10; // Educators/admins more likely from LinkedIn
        administratorScore += 10;
      } else if (referrerDomain.includes('twitter.com')) {
        educatorScore += 5; // Neutral but slight educator bias
      }
    }

    // Check search terms if from search engine
    if (source) {
      detectedSignals.push(`Search source: ${source}`);
      const searchTerms = source.toLowerCase();

      if (searchTerms.includes('classroom') || searchTerms.includes('lesson') || searchTerms.includes('grading')) {
        educatorScore += 15;
      }

      if (
        searchTerms.includes('child') ||
        searchTerms.includes('homework') ||
        searchTerms.includes('parental control')
      ) {
        parentScore += 15;
      }

      if (
        searchTerms.includes('district') ||
        searchTerms.includes('compliance') ||
        searchTerms.includes('enterprise')
      ) {
        administratorScore += 15;
      }
    }

    // Check time of day (teachers often browse during school hours)
    const hour = new Date().getHours();
    if (hour >= 7 && hour <= 15) {
      educatorScore += 5;
      detectedSignals.push('Browsing during school hours');
    } else if (hour >= 19 && hour <= 22) {
      parentScore += 5;
      detectedSignals.push('Browsing during evening hours');
    }

    // Check device type (rough approximation)
    const isMobile = /Mobile|Android|iPhone/i.test(signals.userAgent);
    const isTablet = /iPad|Tablet/i.test(signals.userAgent);

    if (isMobile) {
      parentScore += 5;
      detectedSignals.push('Mobile device');
    } else if (isTablet) {
      educatorScore += 5;
      detectedSignals.push('Tablet device');
    } else {
      administratorScore += 5;
      detectedSignals.push('Desktop device');
    }

    // Check previous visits from cookie
    if (signals.cookieData?.lastVariant) {
      const lastVariant = signals.cookieData.lastVariant;
      detectedSignals.push(`Previous visit as: ${lastVariant}`);

      switch (lastVariant) {
        case 'educator':
          educatorScore += 40;
          break;
        case 'parent':
          parentScore += 40;
          break;
        case 'administrator':
          administratorScore += 40;
          break;
      }
    }

    // Determine variant based on scores
    const maxScore = Math.max(educatorScore, parentScore, administratorScore);
    let variant: MarketingVariant = 'default';
    let confidence = 0;

    if (maxScore > 20) {
      if (educatorScore === maxScore) {
        variant = 'educator';
        confidence = Math.min(educatorScore / 100, 1);
      } else if (parentScore === maxScore) {
        variant = 'parent';
        confidence = Math.min(parentScore / 100, 1);
      } else if (administratorScore === maxScore) {
        variant = 'administrator';
        confidence = Math.min(administratorScore / 100, 1);
      }
    }

    return { variant, confidence, signals: detectedSignals };
  }, []);

  /**
   * Detect visitor type based on current path and signals
   */
  const detectVisitor = useCallback(
    (pathname: string): MarketingVariant => {
      // Check if path explicitly indicates variant
      if (pathname.includes('/educators')) return 'educator';
      if (pathname.includes('/parents')) return 'parent';
      if (pathname.includes('/administrators')) return 'administrator';

      // Otherwise analyze signals
      const signals = collectSignals();
      const result = analyzeSignals(signals);

      // Store detection result
      setDetectedVariant(result.variant);
      setConfidence(result.confidence);
      setSignals(result.signals);

      // Save to cookie for future visits
      const cookieData = {
        lastVariant: result.variant,
        lastVisit: new Date().toISOString(),
        confidence: result.confidence,
      };

      document.cookie = `sigma08_visitor=${encodeURIComponent(JSON.stringify(cookieData))}; path=/; max-age=2592000`; // 30 days

      return result.variant;
    },
    [collectSignals, analyzeSignals]
  );

  /**
   * Track visitor behavior for improving detection
   */
  const trackBehavior = useCallback(
    (action: string, data?: any) => {
      // Send to analytics
      if (window.gtag) {
        window.gtag('event', 'visitor_behavior', {
          event_category: 'Marketing',
          event_label: action,
          detected_variant: detectedVariant,
          confidence,
          ...data,
        });
      }

      // Update cookie with behavior data
      try {
        const cookieValue = document.cookie
          .split('; ')
          .find(row => row.startsWith('sigma08_visitor='))
          ?.split('=')[1];

        if (cookieValue) {
          const cookieData = JSON.parse(decodeURIComponent(cookieValue));
          cookieData.interests = cookieData.interests || [];
          cookieData.interests.push(action);

          document.cookie = `sigma08_visitor=${encodeURIComponent(JSON.stringify(cookieData))}; path=/; max-age=2592000`;
        }
      } catch (e) {
        console.debug('Failed to update visitor cookie');
      }
    },
    [detectedVariant, confidence]
  );

  // Run detection on mount
  useEffect(() => {
    detectVisitor(window.location.pathname);
  }, [detectVisitor]);

  return {
    detectedVariant,
    confidence,
    signals,
    detectVisitor,
    trackBehavior,
  };
};
